/**
 * Enterprise Endpoint Security Module Component
 * Endpoint Compliance Agent Monitoring
 */

import React, { useState, useEffect } from 'react';
import { useI18n } from '../../context/I18nContext';
import { endpointService } from '../../services/enterpriseService';
import { grcService } from '../../services/grcService';
import { Modal, FileUpload, ControlSelector, ComplianceScriptGenerator, useEvidenceIntegration } from './SharedModals';
import {
  Monitor, Laptop, Server, Smartphone, CheckCircle, XCircle, AlertTriangle,
  RefreshCw, Plus, Settings, Search, Filter, Shield, Lock, HardDrive,
  Wifi, WifiOff, Clock, Activity, Eye, Download, Upload, Terminal, LinkIcon
} from 'lucide-react';

interface Endpoint {
  id: number;
  hostname: string;
  device_type: 'laptop' | 'desktop' | 'server' | 'mobile' | 'other';
  os_name: string;
  os_version?: string;
  agent_version?: string;
  agent_status: 'active' | 'inactive' | 'offline' | 'error';
  compliance_status: 'compliant' | 'non_compliant' | 'unknown';
  last_seen_at?: string;
  enrolled_at: string;
  user_id?: number;
  department?: string;
  checks_passed: number;
  checks_failed: number;
  checks_total: number;
}

interface FleetSummary {
  total_endpoints: number;
  active: number;
  offline: number;
  compliant: number;
  non_compliant: number;
  by_os: Record<string, number>;
  by_type: Record<string, number>;
}

export const EnterpriseEndpoints: React.FC<{ frameworkName?: string }> = ({ frameworkName }) => {
  const { language } = useI18n();
  const [endpoints, setEndpoints] = useState<Endpoint[]>([]);
  const [summary, setSummary] = useState<FleetSummary | null>(null);
  const [loading, setLoading] = useState(true);
  const [activeTab, setActiveTab] = useState<'overview' | 'devices' | 'compliance'>('overview');
  const [searchQuery, setSearchQuery] = useState('');
  const [filterStatus, setFilterStatus] = useState<string>('all');

  // Modal states
  const [showDownloadModal, setShowDownloadModal] = useState(false);
  const [showUploadScanModal, setShowUploadScanModal] = useState(false);
  const [showLinkControlsModal, setShowLinkControlsModal] = useState(false);
  const [selectedEndpoint, setSelectedEndpoint] = useState<Endpoint | null>(null);
  const [saving, setSaving] = useState(false);

  // Script generation
  const [selectedOS, setSelectedOS] = useState<'windows' | 'macos' | 'linux'>('windows');

  // Upload scan state
  const [scanFile, setScanFile] = useState<File | null>(null);

  // Control linking
  const [frameworks, setFrameworks] = useState<any[]>([]);
  const [selectedFramework, setSelectedFramework] = useState<number | null>(null);
  const [selectedControls, setSelectedControls] = useState<number[]>([]);
  const { linkEndpointScanToControl } = useEvidenceIntegration();

  useEffect(() => {
    loadData();
  }, []);

  const loadData = async () => {
    setLoading(true);
    try {
      const [endpointsData, summaryData] = await Promise.all([
        endpointService.getEndpoints(),
        endpointService.getFleetSummary().catch(() => null)
      ]);
      
      // Handle API response - may be { data: [...] }, { endpoints: [...] }, or direct array
      const endpointsList = endpointsData?.data || endpointsData?.endpoints || 
        (Array.isArray(endpointsData) ? endpointsData : []);
      setEndpoints(endpointsList);
      setSummary(summaryData?.data || summaryData);
    } catch (error) {
      console.error('Error loading endpoint data:', error);
      setEndpoints([]); // Default to empty array on error
    } finally {
      setLoading(false);
    }
  };

  const loadFrameworks = async () => {
    try {
      const data = await grcService.getFrameworks();
      const frameworkList = Array.isArray(data) ? data : ((data as any)?.frameworks || []);
      setFrameworks(frameworkList);
    } catch (error) {
      console.error('Error loading frameworks:', error);
    }
  };

  const handleUploadScan = async () => {
    if (!scanFile) return;
    setSaving(true);
    try {
      // Parse scan results and update endpoint compliance
      console.log('Processing scan file:', scanFile.name);
      // In production, this would parse the file and update endpoints
      // Process scan file - would parse and call recordCheckResults
      console.log('Processing scan file:', scanFile.name);
      setShowUploadScanModal(false);
      setScanFile(null);
      loadData();
    } catch (error) {
      console.error('Error uploading scan:', error);
    } finally {
      setSaving(false);
    }
  };

  const openLinkModal = (endpoint: Endpoint) => {
    setSelectedEndpoint(endpoint);
    setSelectedControls([]);
    setSelectedFramework(null);
    loadFrameworks();
    setShowLinkControlsModal(true);
  };

  const handleLinkToControls = async () => {
    if (!selectedEndpoint || selectedControls.length === 0) return;
    setSaving(true);
    try {
      for (const controlId of selectedControls) {
        await linkEndpointScanToControl(
          controlId,
          selectedEndpoint.id,
          selectedEndpoint.hostname,
          `${selectedEndpoint.checks_passed}/${selectedEndpoint.checks_total} passed`
        );
      }
      setShowLinkControlsModal(false);
      setSelectedEndpoint(null);
    } catch (error) {
      console.error('Error linking to controls:', error);
    } finally {
      setSaving(false);
    }
  };

  const deviceIcons = {
    laptop: Laptop,
    desktop: Monitor,
    server: Server,
    mobile: Smartphone,
    other: HardDrive
  };

  const statusColors = {
    active: 'bg-green-100 text-green-700 dark:bg-green-900/30 dark:text-green-300',
    inactive: 'bg-yellow-100 text-yellow-700 dark:bg-yellow-900/30 dark:text-yellow-300',
    offline: 'bg-gray-100 text-gray-500 dark:bg-gray-700 dark:text-gray-400',
    error: 'bg-red-100 text-red-700 dark:bg-red-900/30 dark:text-red-300'
  };

  const complianceColors = {
    compliant: 'bg-green-100 text-green-700 dark:bg-green-900/30 dark:text-green-300',
    non_compliant: 'bg-red-100 text-red-700 dark:bg-red-900/30 dark:text-red-300',
    unknown: 'bg-gray-100 text-gray-500 dark:bg-gray-700 dark:text-gray-400'
  };

  const filteredEndpoints = endpoints.filter(endpoint => {
    const matchesSearch = endpoint.hostname.toLowerCase().includes(searchQuery.toLowerCase()) ||
      endpoint.os_name.toLowerCase().includes(searchQuery.toLowerCase());
    const matchesFilter = filterStatus === 'all' || 
      endpoint.agent_status === filterStatus || 
      endpoint.compliance_status === filterStatus;
    return matchesSearch && matchesFilter;
  });

  const getTimeAgo = (dateStr?: string) => {
    if (!dateStr) return language === 'sr' ? 'Nikad' : 'Never';
    const date = new Date(dateStr);
    const now = new Date();
    const diff = Math.floor((now.getTime() - date.getTime()) / 1000);
    
    if (diff < 60) return language === 'sr' ? 'Upravo sada' : 'Just now';
    if (diff < 3600) return `${Math.floor(diff / 60)} min ago`;
    if (diff < 86400) return `${Math.floor(diff / 3600)}h ago`;
    return `${Math.floor(diff / 86400)}d ago`;
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <RefreshCw className="w-8 h-8 animate-spin text-indigo-500" />
      </div>
    );
  }

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <h2 className="text-2xl font-bold text-gray-900 dark:text-white flex items-center gap-3">
            <Monitor className="w-7 h-7 text-emerald-500" />
            {language === 'sr' ? 'Sigurnost krajnjih tačaka' : 'Endpoint Security'}
            {frameworkName && (
              <span className="px-3 py-1 bg-emerald-100 dark:bg-emerald-900/30 text-emerald-700 dark:text-emerald-300 rounded-full text-sm font-medium">
                {frameworkName}
              </span>
            )}
          </h2>
          <p className="text-gray-500 dark:text-gray-400 mt-1">
            {language === 'sr' 
              ? 'Pratite usklađenost uređaja u vašoj floti'
              : 'Monitor device compliance across your fleet'
            }
          </p>
        </div>
        <div className="flex items-center gap-2">
          <button 
            onClick={() => setShowUploadScanModal(true)}
            className="flex items-center gap-2 px-4 py-2 border border-emerald-600 text-emerald-600 rounded-lg hover:bg-emerald-50 dark:hover:bg-emerald-900/20 transition-colors"
          >
            <Upload className="w-4 h-4" />
            {language === 'sr' ? 'Upload skena' : 'Upload Scan'}
          </button>
          <button 
            onClick={() => setShowDownloadModal(true)}
            className="flex items-center gap-2 px-4 py-2 bg-emerald-600 text-white rounded-lg hover:bg-emerald-700 transition-colors"
          >
            <Download className="w-4 h-4" />
            {language === 'sr' ? 'Preuzmi agenta' : 'Download Agent'}
          </button>
        </div>
      </div>

      {/* Stats Cards */}
      <div className="grid grid-cols-1 md:grid-cols-5 gap-4">
        <div className="bg-white dark:bg-gray-800 rounded-xl border border-gray-200 dark:border-gray-700 p-5">
          <div className="flex items-center justify-between mb-3">
            <Monitor className="w-8 h-8 text-emerald-500" />
          </div>
          <div className="text-3xl font-bold text-gray-900 dark:text-white">{endpoints.length}</div>
          <div className="text-sm text-gray-500 dark:text-gray-400">{language === 'sr' ? 'Ukupno uređaja' : 'Total Devices'}</div>
        </div>
        
        <div className="bg-white dark:bg-gray-800 rounded-xl border border-gray-200 dark:border-gray-700 p-5">
          <div className="flex items-center justify-between mb-3">
            <Wifi className="w-8 h-8 text-green-500" />
          </div>
          <div className="text-3xl font-bold text-gray-900 dark:text-white">
            {endpoints.filter(e => e.agent_status === 'active').length}
          </div>
          <div className="text-sm text-gray-500 dark:text-gray-400">{language === 'sr' ? 'Online' : 'Online'}</div>
        </div>
        
        <div className="bg-white dark:bg-gray-800 rounded-xl border border-gray-200 dark:border-gray-700 p-5">
          <div className="flex items-center justify-between mb-3">
            <WifiOff className="w-8 h-8 text-gray-400" />
          </div>
          <div className="text-3xl font-bold text-gray-900 dark:text-white">
            {endpoints.filter(e => e.agent_status === 'offline').length}
          </div>
          <div className="text-sm text-gray-500 dark:text-gray-400">{language === 'sr' ? 'Offline' : 'Offline'}</div>
        </div>
        
        <div className="bg-white dark:bg-gray-800 rounded-xl border border-gray-200 dark:border-gray-700 p-5">
          <div className="flex items-center justify-between mb-3">
            <CheckCircle className="w-8 h-8 text-emerald-500" />
          </div>
          <div className="text-3xl font-bold text-gray-900 dark:text-white">
            {endpoints.filter(e => e.compliance_status === 'compliant').length}
          </div>
          <div className="text-sm text-gray-500 dark:text-gray-400">{language === 'sr' ? 'Usklađeno' : 'Compliant'}</div>
        </div>
        
        <div className="bg-white dark:bg-gray-800 rounded-xl border border-gray-200 dark:border-gray-700 p-5">
          <div className="flex items-center justify-between mb-3">
            <XCircle className="w-8 h-8 text-red-500" />
          </div>
          <div className="text-3xl font-bold text-gray-900 dark:text-white">
            {endpoints.filter(e => e.compliance_status === 'non_compliant').length}
          </div>
          <div className="text-sm text-gray-500 dark:text-gray-400">{language === 'sr' ? 'Neusklađeno' : 'Non-Compliant'}</div>
        </div>
      </div>

      {/* Tabs */}
      <div className="flex gap-2 border-b border-gray-200 dark:border-gray-700">
        {[
          { key: 'overview', icon: Activity, label: language === 'sr' ? 'Pregled' : 'Overview' },
          { key: 'devices', icon: Monitor, label: language === 'sr' ? 'Uređaji' : 'Devices' },
          { key: 'compliance', icon: Shield, label: language === 'sr' ? 'Usklađenost' : 'Compliance' }
        ].map(tab => (
          <button
            key={tab.key}
            onClick={() => setActiveTab(tab.key as typeof activeTab)}
            className={`flex items-center gap-2 px-4 py-3 border-b-2 transition-colors ${
              activeTab === tab.key
                ? 'border-emerald-500 text-emerald-600 dark:text-emerald-400'
                : 'border-transparent text-gray-500 hover:text-gray-700 dark:text-gray-400 dark:hover:text-gray-200'
            }`}
          >
            <tab.icon className="w-4 h-4" />
            {tab.label}
          </button>
        ))}
      </div>

      {/* Overview Tab */}
      {activeTab === 'overview' && (
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
          {/* Fleet Health */}
          <div className="bg-white dark:bg-gray-800 rounded-xl border border-gray-200 dark:border-gray-700 p-5">
            <h3 className="text-lg font-semibold text-gray-900 dark:text-white mb-4">
              {language === 'sr' ? 'Zdravlje flote' : 'Fleet Health'}
            </h3>
            <div className="space-y-4">
              <div>
                <div className="flex items-center justify-between mb-1">
                  <span className="text-sm text-gray-600 dark:text-gray-400">{language === 'sr' ? 'Agent aktivnost' : 'Agent Activity'}</span>
                  <span className="text-sm font-semibold">
                    {Math.round((endpoints.filter(e => e.agent_status === 'active').length / endpoints.length) * 100) || 0}%
                  </span>
                </div>
                <div className="h-2 bg-gray-100 dark:bg-gray-700 rounded-full">
                  <div 
                    className="h-2 bg-green-500 rounded-full" 
                    style={{ width: `${(endpoints.filter(e => e.agent_status === 'active').length / endpoints.length) * 100 || 0}%` }}
                  />
                </div>
              </div>
              <div>
                <div className="flex items-center justify-between mb-1">
                  <span className="text-sm text-gray-600 dark:text-gray-400">{language === 'sr' ? 'Usklađenost' : 'Compliance'}</span>
                  <span className="text-sm font-semibold">
                    {Math.round((endpoints.filter(e => e.compliance_status === 'compliant').length / endpoints.length) * 100) || 0}%
                  </span>
                </div>
                <div className="h-2 bg-gray-100 dark:bg-gray-700 rounded-full">
                  <div 
                    className="h-2 bg-emerald-500 rounded-full" 
                    style={{ width: `${(endpoints.filter(e => e.compliance_status === 'compliant').length / endpoints.length) * 100 || 0}%` }}
                  />
                </div>
              </div>
            </div>

            {/* OS Distribution */}
            <h4 className="text-sm font-medium text-gray-700 dark:text-gray-300 mt-6 mb-3">
              {language === 'sr' ? 'Po operativnom sistemu' : 'By Operating System'}
            </h4>
            <div className="grid grid-cols-2 gap-2">
              {Object.entries(
                endpoints.reduce((acc, e) => {
                  acc[e.os_name] = (acc[e.os_name] || 0) + 1;
                  return acc;
                }, {} as Record<string, number>)
              ).slice(0, 4).map(([os, count]) => (
                <div key={os} className="flex items-center justify-between p-2 bg-gray-50 dark:bg-gray-700/50 rounded">
                  <span className="text-sm text-gray-600 dark:text-gray-400">{os}</span>
                  <span className="text-sm font-medium text-gray-900 dark:text-white">{count}</span>
                </div>
              ))}
            </div>
          </div>

          {/* Non-Compliant Devices */}
          <div className="bg-white dark:bg-gray-800 rounded-xl border border-gray-200 dark:border-gray-700 p-5">
            <h3 className="text-lg font-semibold text-gray-900 dark:text-white mb-4 flex items-center gap-2">
              <AlertTriangle className="w-5 h-5 text-red-500" />
              {language === 'sr' ? 'Zahtijevaju pažnju' : 'Require Attention'}
            </h3>
            <div className="space-y-3">
              {endpoints.filter(e => e.compliance_status === 'non_compliant' || e.agent_status === 'error').slice(0, 5).map(endpoint => {
                const DeviceIcon = deviceIcons[endpoint.device_type] || Monitor;
                return (
                  <div key={endpoint.id} className="flex items-center justify-between p-3 bg-gray-50 dark:bg-gray-700/50 rounded-lg">
                    <div className="flex items-center gap-3">
                      <div className="w-10 h-10 bg-red-100 dark:bg-red-900/30 rounded-lg flex items-center justify-center">
                        <DeviceIcon className="w-5 h-5 text-red-600" />
                      </div>
                      <div>
                        <div className="font-medium text-gray-900 dark:text-white">{endpoint.hostname}</div>
                        <div className="text-xs text-gray-500">
                          {endpoint.checks_failed} {language === 'sr' ? 'neuspjelih provjera' : 'failed checks'}
                        </div>
                      </div>
                    </div>
                    <button className="p-2 text-gray-400 hover:bg-gray-100 dark:hover:bg-gray-700 rounded-lg">
                      <Eye className="w-4 h-4" />
                    </button>
                  </div>
                );
              })}
              {endpoints.filter(e => e.compliance_status === 'non_compliant' || e.agent_status === 'error').length === 0 && (
                <div className="text-center py-4 text-gray-500 dark:text-gray-400">
                  <CheckCircle className="w-8 h-8 mx-auto mb-2 text-green-500" />
                  <p>{language === 'sr' ? 'Svi uređaji su usklađeni' : 'All devices are compliant'}</p>
                </div>
              )}
            </div>
          </div>
        </div>
      )}

      {/* Devices Tab */}
      {activeTab === 'devices' && (
        <div className="space-y-4">
          {/* Search & Filter */}
          <div className="flex items-center gap-4">
            <div className="flex-1 relative">
              <Search className="w-5 h-5 absolute left-3 top-1/2 -translate-y-1/2 text-gray-400" />
              <input
                type="text"
                value={searchQuery}
                onChange={(e) => setSearchQuery(e.target.value)}
                placeholder={language === 'sr' ? 'Pretraži uređaje...' : 'Search devices...'}
                className="w-full pl-10 pr-4 py-2 border border-gray-200 dark:border-gray-700 rounded-lg bg-white dark:bg-gray-800 text-gray-900 dark:text-white"
              />
            </div>
            <select
              value={filterStatus}
              onChange={(e) => setFilterStatus(e.target.value)}
              className="px-4 py-2 border border-gray-200 dark:border-gray-700 rounded-lg bg-white dark:bg-gray-800 text-gray-700 dark:text-gray-300"
            >
              <option value="all">{language === 'sr' ? 'Svi statusi' : 'All Status'}</option>
              <option value="active">{language === 'sr' ? 'Aktivni' : 'Active'}</option>
              <option value="offline">{language === 'sr' ? 'Offline' : 'Offline'}</option>
              <option value="compliant">{language === 'sr' ? 'Usklađeni' : 'Compliant'}</option>
              <option value="non_compliant">{language === 'sr' ? 'Neusklađeni' : 'Non-Compliant'}</option>
            </select>
          </div>

          {/* Devices Table */}
          <div className="bg-white dark:bg-gray-800 rounded-xl border border-gray-200 dark:border-gray-700 overflow-hidden">
            <table className="w-full">
              <thead className="bg-gray-50 dark:bg-gray-700/50">
                <tr>
                  <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-400 uppercase">{language === 'sr' ? 'Uređaj' : 'Device'}</th>
                  <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-400 uppercase">{language === 'sr' ? 'OS' : 'OS'}</th>
                  <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-400 uppercase">{language === 'sr' ? 'Status' : 'Status'}</th>
                  <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-400 uppercase">{language === 'sr' ? 'Usklađenost' : 'Compliance'}</th>
                  <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-400 uppercase">{language === 'sr' ? 'Viđen' : 'Last Seen'}</th>
                  <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-400 uppercase">{language === 'sr' ? 'Provjere' : 'Checks'}</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-gray-200 dark:divide-gray-700">
                {filteredEndpoints.map(endpoint => {
                  const DeviceIcon = deviceIcons[endpoint.device_type] || Monitor;
                  return (
                    <tr key={endpoint.id} className="hover:bg-gray-50 dark:hover:bg-gray-700/50">
                      <td className="px-4 py-3">
                        <div className="flex items-center gap-3">
                          <DeviceIcon className="w-5 h-5 text-gray-500" />
                          <span className="text-sm font-medium text-gray-900 dark:text-white">{endpoint.hostname}</span>
                        </div>
                      </td>
                      <td className="px-4 py-3 text-sm text-gray-500 dark:text-gray-400">{endpoint.os_name}</td>
                      <td className="px-4 py-3">
                        <span className={`px-2 py-1 rounded text-xs ${statusColors[endpoint.agent_status]}`}>
                          {endpoint.agent_status}
                        </span>
                      </td>
                      <td className="px-4 py-3">
                        <span className={`px-2 py-1 rounded text-xs ${complianceColors[endpoint.compliance_status]}`}>
                          {endpoint.compliance_status}
                        </span>
                      </td>
                      <td className="px-4 py-3 text-sm text-gray-500">{getTimeAgo(endpoint.last_seen_at)}</td>
                      <td className="px-4 py-3 text-sm">
                        <div className="flex items-center gap-2">
                          <span className="text-green-600">{endpoint.checks_passed}</span>
                          <span className="text-gray-400">/</span>
                          <span className="text-red-600">{endpoint.checks_failed}</span>
                          <button 
                            onClick={() => openLinkModal(endpoint)}
                            className="ml-2 p-1 text-indigo-400 hover:text-indigo-600" 
                            title={language === 'sr' ? 'Poveži sa kontrolama' : 'Link to controls'}
                          >
                            <LinkIcon className="w-4 h-4" />
                          </button>
                        </div>
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
            {filteredEndpoints.length === 0 && (
              <p className="text-center py-8 text-gray-500 dark:text-gray-400">
                {language === 'sr' ? 'Nema uređaja' : 'No devices found'}
              </p>
            )}
          </div>
        </div>
      )}

      {/* Compliance Tab */}
      {activeTab === 'compliance' && (
        <div className="bg-white dark:bg-gray-800 rounded-xl border border-gray-200 dark:border-gray-700 p-5">
          <div className="mb-6">
            <h3 className="text-lg font-semibold text-gray-900 dark:text-white mb-2">
              {language === 'sr' ? 'Provjera usklađenosti krajnjih tačaka' : 'Endpoint Compliance Checking'}
            </h3>
            <p className="text-sm text-gray-600 dark:text-gray-400 mb-4">
              {language === 'sr' 
                ? 'Generiši skriptu za provjeru usklađenosti ili uploaduj rezultate skeniranja'
                : 'Generate a compliance check script or upload scan results'
              }
            </p>
            <div className="flex gap-3">
              <button 
                onClick={() => setShowDownloadModal(true)}
                className="flex items-center gap-2 px-4 py-2 bg-indigo-600 text-white rounded-lg hover:bg-indigo-700"
              >
                <Terminal className="w-4 h-4" />
                {language === 'sr' ? 'Generiši skriptu' : 'Generate Script'}
              </button>
              <button 
                onClick={() => setShowUploadScanModal(true)}
                className="flex items-center gap-2 px-4 py-2 border border-gray-300 dark:border-gray-600 text-gray-700 dark:text-gray-300 rounded-lg hover:bg-gray-50 dark:hover:bg-gray-700"
              >
                <Upload className="w-4 h-4" />
                {language === 'sr' ? 'Upload rezultata' : 'Upload Results'}
              </button>
            </div>
          </div>
          <div className="border-t border-gray-200 dark:border-gray-700 pt-4">
            <h4 className="text-md font-medium text-gray-900 dark:text-white mb-3">
              {language === 'sr' ? 'Status usklađenosti po regulativi' : 'Compliance Status by Framework'}
            </h4>
            <p className="text-gray-500 dark:text-gray-400 text-sm">
              {language === 'sr' 
                ? 'Povežite rezultate skeniranja sa kontrolama regulative da biste pratili usklađenost'
                : 'Link scan results to framework controls to track compliance'
              }
            </p>
          </div>
        </div>
      )}

      {/* Download Agent / Generate Script Modal */}
      <Modal
        isOpen={showDownloadModal}
        onClose={() => setShowDownloadModal(false)}
        title={language === 'sr' ? 'Preuzmi agenta / Generiši skriptu' : 'Download Agent / Generate Script'}
        size="lg"
      >
        <div className="space-y-4">
          <p className="text-sm text-gray-600 dark:text-gray-400">
            {language === 'sr' 
              ? 'Odaberite operativni sistem i generiši skriptu za provjeru usklađenosti krajnjih tačaka.'
              : 'Select operating system and generate a script to check endpoint compliance.'
            }
          </p>
          <div className="flex gap-2">
            {[
              { key: 'windows', label: 'Windows' },
              { key: 'macos', label: 'macOS' },
              { key: 'linux', label: 'Linux' }
            ].map(os => (
              <button
                key={os.key}
                onClick={() => setSelectedOS(os.key as typeof selectedOS)}
                className={`px-4 py-2 rounded-lg border transition-colors ${
                  selectedOS === os.key
                    ? 'border-emerald-500 bg-emerald-50 text-emerald-700 dark:bg-emerald-900/30 dark:text-emerald-300'
                    : 'border-gray-300 dark:border-gray-600 text-gray-700 dark:text-gray-300'
                }`}
              >
                {os.label}
              </button>
            ))}
          </div>
          <ComplianceScriptGenerator
            platform={selectedOS}
            checks={[
              { name: 'Disk Encryption', description: 'Check if full disk encryption is enabled' },
              { name: 'Firewall Status', description: 'Verify firewall is active and configured' },
              { name: 'Antivirus Status', description: 'Check antivirus is installed and updated' },
              { name: 'OS Updates', description: 'Verify latest security updates are installed' },
              { name: 'Screen Lock', description: 'Check screen lock timeout is configured' },
              { name: 'USB Policy', description: 'Verify USB storage restrictions' }
            ]}
          />
          <div className="p-3 bg-blue-50 dark:bg-blue-900/20 rounded-lg">
            <p className="text-sm text-blue-700 dark:text-blue-300">
              <strong>{language === 'sr' ? 'Upotreba:' : 'Usage:'}</strong>{' '}
              {language === 'sr' 
                ? 'Pokrenite skriptu na krajnjim tačkama i upload-ujte rezultate nazad u sistem.'
                : 'Run the script on endpoints and upload the results back to the system.'
              }
            </p>
          </div>
        </div>
      </Modal>

      {/* Upload Scan Results Modal */}
      <Modal
        isOpen={showUploadScanModal}
        onClose={() => setShowUploadScanModal(false)}
        title={language === 'sr' ? 'Upload rezultata skeniranja' : 'Upload Scan Results'}
        footer={
          <div className="flex justify-end gap-3">
            <button
              onClick={() => setShowUploadScanModal(false)}
              className="px-4 py-2 text-gray-700 dark:text-gray-300 border border-gray-300 dark:border-gray-600 rounded-lg hover:bg-gray-50 dark:hover:bg-gray-700"
            >
              {language === 'sr' ? 'Otkaži' : 'Cancel'}
            </button>
            <button
              onClick={handleUploadScan}
              disabled={saving || !scanFile}
              className="px-4 py-2 bg-emerald-600 text-white rounded-lg hover:bg-emerald-700 disabled:opacity-50"
            >
              {saving ? (language === 'sr' ? 'Obrada...' : 'Processing...') : (language === 'sr' ? 'Upload' : 'Upload')}
            </button>
          </div>
        }
      >
        <div className="space-y-4">
          <p className="text-sm text-gray-600 dark:text-gray-400">
            {language === 'sr' 
              ? 'Upload-ujte rezultate skeniranja u JSON ili CSV formatu. Rezultati će biti povezani sa relevantnim kontrolama.'
              : 'Upload scan results in JSON or CSV format. Results will be linked to relevant controls.'
            }
          </p>
          <FileUpload
            accept=".json,.csv,.xml"
            onFileSelect={setScanFile}
            label={language === 'sr' ? 'Rezultati skeniranja (JSON, CSV, XML)' : 'Scan Results (JSON, CSV, XML)'}
          />
          <div className="p-3 bg-gray-50 dark:bg-gray-700 rounded-lg">
            <p className="text-sm text-gray-600 dark:text-gray-400 mb-2">
              {language === 'sr' ? 'Očekivani format:' : 'Expected format:'}
            </p>
            <pre className="text-xs text-gray-500 dark:text-gray-400 overflow-x-auto">
{`{
  "hostname": "LAPTOP-001",
  "checks": [
    { "name": "disk_encryption", "passed": true },
    { "name": "firewall", "passed": true },
    { "name": "antivirus", "passed": false }
  ]
}`}
            </pre>
          </div>
        </div>
      </Modal>

      {/* Link to Controls Modal */}
      <Modal
        isOpen={showLinkControlsModal}
        onClose={() => setShowLinkControlsModal(false)}
        title={language === 'sr' ? 'Poveži rezultate sa kontrolama' : 'Link Results to Controls'}
        footer={
          <div className="flex justify-end gap-3">
            <button
              onClick={() => setShowLinkControlsModal(false)}
              className="px-4 py-2 text-gray-700 dark:text-gray-300 border border-gray-300 dark:border-gray-600 rounded-lg hover:bg-gray-50 dark:hover:bg-gray-700"
            >
              {language === 'sr' ? 'Otkaži' : 'Cancel'}
            </button>
            <button
              onClick={handleLinkToControls}
              disabled={saving || selectedControls.length === 0}
              className="px-4 py-2 bg-indigo-600 text-white rounded-lg hover:bg-indigo-700 disabled:opacity-50"
            >
              {saving ? (language === 'sr' ? 'Čuvanje...' : 'Saving...') : (language === 'sr' ? 'Poveži' : 'Link')}
            </button>
          </div>
        }
      >
        {selectedEndpoint && (
          <div className="space-y-4">
            <div className="p-3 bg-gray-50 dark:bg-gray-700 rounded-lg">
              <div className="flex items-center gap-3">
                <Monitor className="w-5 h-5 text-gray-500" />
                <div>
                  <div className="font-medium text-gray-900 dark:text-white">{selectedEndpoint.hostname}</div>
                  <div className="text-sm text-gray-500">
                    {selectedEndpoint.checks_passed}/{selectedEndpoint.checks_total} {language === 'sr' ? 'provjera prošlo' : 'checks passed'}
                  </div>
                </div>
                <span className={`ml-auto px-2 py-1 rounded text-xs ${complianceColors[selectedEndpoint.compliance_status]}`}>
                  {selectedEndpoint.compliance_status}
                </span>
              </div>
            </div>
            <p className="text-sm text-gray-600 dark:text-gray-400">
              {language === 'sr' 
                ? 'Rezultati usklađenosti ovog uređaja će biti dodati kao dokaz za odabrane kontrole'
                : 'This device compliance results will be added as evidence to selected controls'
              }
            </p>
            <ControlSelector
              frameworks={frameworks}
              selectedFramework={selectedFramework}
              onSelectFramework={setSelectedFramework}
              selectedControls={selectedControls}
              onSelectControls={setSelectedControls}
            />
          </div>
        )}
      </Modal>
    </div>
  );
};

export default EnterpriseEndpoints;
